/* global window */
import type {
  RailtownJsonToken,
  RailtownPayload,
  RailtownPayloadBody,
  RailtownPayloadBodyProperties,
} from "@railtownai/utils";
import type {
  RailtownBrowserConfig,
  RailtownBrowserUserProperties,
  SendParams,
} from "./types";

import {
  escape,
  getBrowserName,
  getLogLevel,
  _uuid,
  shouldIgnoreError,
  excludeReservedKeysFromUserProperties,
} from "@railtownai/utils";

import { RateLimiter } from "./rate-limiter";

class RailtownAI {
  isInstalled = false;

  shouldListenForNetworkErrors = true;
  shouldListenForUnhandledRejectionErrors = true;
  shouldListenForGlobalErrors = true;

  ignoreErrors: (string | RegExp)[] = ["visualstudio.com/v2/track"];

  // Simple Rate Limiting Implementation
  private rateLimiter?: RateLimiter | null;

  RAILTOWN_VERSION = `Railtown.Ingest.Logs.Client.Javascript 2.0.28`; // should match package.json for @railtownai/browser after running lerna
  RAILTOWN_SESSION_KEY = "railtowntrackingcorrelation";
  RAILTOWN_SESSION_KEY_LENGTH = this.RAILTOWN_SESSION_KEY.length + 1;

  sessionId = "";
  gRequest = [];
  gUserUrl: string | undefined = "";
  gJsonParseToken: RailtownJsonToken = { o: "", p: "", e: "", u: "", h: "" };
  gCustomProperties = {};

  constructor(config?: RailtownBrowserConfig) {
    this.shouldListenForNetworkErrors = config?.shouldListenForNetworkErrors ?? true;
    this.shouldListenForUnhandledRejectionErrors =
      config?.shouldListenForUnhandledRejectionErrors ?? true;
    this.shouldListenForGlobalErrors = config?.shouldListenForGlobalErrors ?? true;

    this.rateLimiter =
      config?.rateLimiter ??
      new RateLimiter({ maxInvocationsCount: 10, burstWindowDurationInMS: 1000 });
  }

  /**
   * Installs the Railtown AI JS Logger
   *
   * @param {string} key - The Railtown AI API Key
   * @param {RailtownBrowserUserProperties} userProperties - Allows users to add custom properties to logs
   * @param {string} userUrl - Allows users to proxy logs to their own server
   * @returns {void}
   */
  install(
    key?: string,
    userProperties?: RailtownBrowserUserProperties,
    userUrl?: string
  ) {
    this.gCustomProperties = userProperties || {};

    let token = "";
    let base64ParseToken;
    let jsonParseToken = "";

    if (!key) {
      return console.warn("Please add your railtown api key");
    }
    if (typeof this.gCustomProperties !== "object") {
      return console.warn("Properties are not an object");
    }

    if (userUrl && typeof userUrl !== "string") {
      return console.warn("Invalid url provided");
    }

    if (userProperties?.ignoreErrors) {
      this.ignoreErrors = userProperties.ignoreErrors;
    }

    token = key;
    this.gUserUrl = userUrl;

    if (this.rateLimiter) {
      console.debug("Railtown AI Logger Rate limiter is enabled");
    }

    try {
      base64ParseToken = atob(token);
      jsonParseToken = JSON.parse(base64ParseToken);
    } catch {
      return console.warn("Unable to parse railtown.ai api token");
    }

    // @ts-expect-error jsonParseToken is an object not a string
    this.gJsonParseToken = jsonParseToken;
    this.setSession();
    this.listenForErrors();
    this.isInstalled = true;
  }

  readCookie(): string {
    const cookie = document.cookie,
      i = cookie.indexOf(this.RAILTOWN_SESSION_KEY);
    if (i < 0) {
      return "";
    } else {
      let end = cookie.indexOf(";", i + 1);
      end = end < 0 ? cookie.length : end;
      return cookie.slice(i + this.RAILTOWN_SESSION_KEY_LENGTH, end);
    }
  }

  setCookie(value: string): void {
    document.cookie = this.RAILTOWN_SESSION_KEY + "=" + value;
  }

  setSession(sessionId?: string) {
    if (sessionId) {
      this.sessionId = sessionId;
      this.setCookie(this.sessionId);
    } else if (!this.sessionId) {
      this.sessionId = this.readCookie();
      if (!this.sessionId) {
        this.sessionId = _uuid();
        this.setCookie(this.sessionId);
      }
    }
  }

  /**
   * Gets invoked after log() but before sending the request to Railtown AI logs API
   * Typically used for any pre-flight validation or modification of the log object
   *
   * @param {string} type - The type of log to send to Railtown AI {ClientLog, GlobalError, NetworkError, unhandledRejection}
   * @param {SendParams} params - The log object to send to Railtown AI
   */
  send(type: string, params: SendParams) {
    // If we are ignoring the error, don't send the log
    if (shouldIgnoreError(params.message, this.ignoreErrors)) {
      return false;
    }

    // Only rate limit if we have a rate limiter passed into logger
    if (this.rateLimiter) {
      return this.rateLimiter.limitLogCall(type, params, (type, params) =>
        this.sendRequest(type, params)
      );
    }

    // If no rate limiter, just send the log
    return this.sendRequest(type, params);
  }

  /**
   *
   * @param {string} type - Type of Log (ClientLog, GlobalError, NetworkError, unhandledRejection)
   * @param params
   * @returns Promise<void>
   */
  private async sendRequest(type: string, params: Record<string, any>): Promise<void> {
    try {
      const url =
        this.gUserUrl && this.gUserUrl !== "undefined"
          ? this.gUserUrl
          : "https://" + this.gJsonParseToken.u;

      let feProperties = {};
      const SessionId = this.readCookie();

      if (type === "GlobalError") {
        feProperties = {
          FileName: params.filename,
          LineNo: params.lineno,
          ColNo: params.colno,
        };
      } else if (type === "NetworkError" && params.thirdPartyScript) {
        feProperties = {
          ThirdPartyOrigin: params.thirdPartyScript,
        };
      }

      const additionalProperties: RailtownPayloadBodyProperties = Object.assign(
        this.gCustomProperties,
        feProperties,
        params.properties,
        {
          Url: window.location.href,
          UserAgent: navigator.userAgent,
          Browser: getBrowserName(),
          Viewport:
            Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0) +
            " " +
            Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0),
          RailtownCorrelationId: SessionId,
          Type: type,
        }
      );

      const body: RailtownPayloadBody = {
        Message: params.message,
        Level: params.level,
        OrganizationId: this.gJsonParseToken.o,
        ProjectId: this.gJsonParseToken.p,
        EnvironmentId: this.gJsonParseToken.e,
        Runtime: "v8-javascript",
        Exception: params.exception
          ? params.exception +
            "\n    at " +
            type +
            " (" +
            window.location.pathname.split("?")[0] +
            ":0:0)"
          : "",
        TimeStamp: new Date().toISOString(),
        Properties: excludeReservedKeysFromUserProperties(additionalProperties),
      };

      const connectionName =
        this.gUserUrl && this.gUserUrl !== "undefined"
          ? this.gUserUrl
          : this.gJsonParseToken.u;

      const payload: RailtownPayload[] = [
        {
          Body: escape(body),
          UserProperties: {
            AuthenticationCode: this.gJsonParseToken.h,
            ClientVersion: this.RAILTOWN_VERSION,
            Encoding: "utf-8",
            ConnectionName: connectionName,
          },
        },
      ];

      await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "User-Agent": "railtown-js(browser)",
        },
        body: JSON.stringify(payload),
      });
    } catch {
      return console.warn("Unable to send log to railtown.ai");
    }
  }

  log(log: any, level: string) {
    if (!log.message) {
      return console.warn("Please add a message to your railtown.ai log");
    }
    let stackTrace = "";

    if (log.stackTrace) {
      if ("object" === typeof log.stackTrace) {
        stackTrace = log.stackTrace.stack || "n/a";
      } else {
        return console.warn("Invalid stacktrace type");
      }
    } else if (log.stack) {
      stackTrace = log.stack;
    }

    const params: SendParams = {
      level: getLogLevel(level),
      message: log.message,
      properties: log.properties || {},
      exception: stackTrace,
    };

    this.send("ClientLog", params);
  }

  trace(e: unknown) {
    this.log(e, "trace");
  }
  debug(e: unknown) {
    this.log(e, "debug");
  }
  info(e: unknown) {
    this.log(e, "info");
  }
  warn(e: unknown) {
    this.log(e, "warn");
  }
  error(e: unknown) {
    this.log(e, "error");
  }
  fatal(e: unknown) {
    this.log(e, "fatal");
  }
  parseUrl(url: string) {
    try {
      const newUrl = new URL(url);
      const isThirdPartyScript = newUrl.hostname !== window.location.hostname;

      let path = "";
      let lastHost = "";

      // parse url depending on if third party script
      if (isThirdPartyScript) {
        lastHost = newUrl.hostname.substring(
          newUrl.hostname.lastIndexOf(".", newUrl.hostname.lastIndexOf(".") - 1)
        );
        lastHost = lastHost.charAt(0) === "." ? lastHost.replace(".", "") : lastHost;
        path = newUrl.pathname;
      } else {
        lastHost = "~";
        path = "/" + newUrl.pathname.substring(newUrl.pathname.lastIndexOf("/") + 1);
      }

      return { url: lastHost + path, isThirdPartyScript };
    } catch {
      return { url, isThirdPartyScript: false };
    }
  }

  removeListeners() {
    window.removeEventListener("error", this.listenForErrors);
  }

  listenForErrors() {
    const self = this;

    if (this.shouldListenForNetworkErrors) {
      // Store and overwrite the XMLHttpRequest open native method
      const open = XMLHttpRequest.prototype.open;

      // @ts-expect-error XMLHttpRequest is a global object
      XMLHttpRequest.prototype.open = function (...args) {
        const xhrInstance = this;
        xhrInstance.addEventListener("load", function (event) {
          // @ts-expect-error status is a number
          const eventTargetStatus = event.currentTarget.status;
          // @ts-expect-error responseURL is a string
          const eventTargetResponseUrl = event.currentTarget.responseURL;
          // @ts-expect-error response is a string
          const eventTargetResponse = event.currentTarget.response;

          if (eventTargetStatus >= 400) {
            if (self.gUserUrl && eventTargetResponseUrl.indexOf(self.gUserUrl) > -1) {
              return console.warn("invalid", self.gUserUrl);
            }

            const parsedUrl = self.parseUrl(eventTargetResponseUrl);

            const params = {
              message: eventTargetStatus + ": " + eventTargetResponseUrl,
              level: 4,
              exception:
                eventTargetStatus + " from " + parsedUrl.url + "\n" + eventTargetResponse,
              thirdPartyScript: parsedUrl.isThirdPartyScript,
            };

            self.send("NetworkError", params);
          }
        });

        return open.apply(xhrInstance, args);
      };
    }

    if (this.shouldListenForUnhandledRejectionErrors) {
      window.addEventListener(
        "unhandledrejection",
        function (event: PromiseRejectionEvent) {
          const { reason } = event || {};

          const params = {
            message: "Promise " + event.type + " " + reason.message,
            level: getLogLevel(reason.type),
            exception: reason?.stack ? reason.stack : reason,
          };

          self.send("unhandledRejection", params);
        }
      );
    }

    if (this.shouldListenForGlobalErrors) {
      window.addEventListener("error", function (event) {
        const { message, filename, lineno, colno, error, type } = event || {};

        // Skip the first error, it is always irrelevant in the DEV mode.
        if (
          error?.stack?.indexOf("invokeGuardedCallbackDev") >= 0 &&
          !error?.alreadySeen
        ) {
          error.alreadySeen = true;
          event.preventDefault();
          return;
        }

        const params = {
          message: message,
          level: getLogLevel(type),
          exception: error?.stack ? error.stack : error,
          filename,
          lineno,
          colno,
          error,
        };

        self.send("GlobalError", params);
      });
    }
  }
}
export { RailtownAI };
